import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Popup.css";
import { ThemeContext } from "../context/themeContext";
import { X, Clock } from "lucide-react";

const featureInfo = {
  Investors: {
    tagline: "Connect with investors who fit your vision",
    description:
      "A curated network of angel investors, VCs, and micro-funds matched to your startup's stage, sector, and validation score. No cold emails — just warm, relevant introductions.",
    why: "Most founders spend 6+ months chasing the wrong investors. Smart matching cuts that time dramatically and puts your pitch in front of people already looking for what you're building.",
  },
  "Investor Matching": {
    tagline: "Get matched to the right investor — not just any investor",
    description:
      "MarsMate AI analyses your validated idea, industry, funding stage, and traction signals to surface a curated shortlist of angel investors, VCs, and micro-funds whose thesis aligns with your startup's DNA. No spray-and-pray cold outreach — just precision-matched introductions backed by your validation score.",
    why: "The average founder pitches 50+ investors before raising. Most of that effort is wasted on mismatched capital. Investor Matching cuts that down dramatically by connecting you only to investors actively deploying in your space, stage, and geography.",
  },
  Registration: {
    tagline: "One-click startup registration & compliance",
    description:
      "Guided company registration, legal entity setup, and compliance checklist tailored to your country and business model — all from inside MarsMate.",
    why: "Legal and administrative overhead kills early momentum. A streamlined registration flow keeps founders focused on building, not bureaucracy.",
  },
  "Idea Refinement": {
    tagline: "Sharpen your idea with AI-powered iteration",
    description:
      "Go beyond validation — get targeted suggestions to strengthen your core concept, identify blind spots, reposition for better market fit, and stress-test your assumptions before you commit resources.",
    why: "A validated idea is just the start. Continuous refinement separates ideas that gain traction from those that stall. Iteration informed by data beats gut feel every time.",
  },
  Investor: {
    tagline: "Find investors who believe in your vision",
    description:
      "AI-curated investor matches based on your idea validation score, industry, and funding stage. Skip cold outreach and connect with capital that aligns with your startup's DNA.",
    why: "Most founders waste months pitching the wrong investors. Targeted matching saves time and dramatically increases your chances of a successful raise.",
  },
  B2B: {
    tagline: "Unlock strategic business partnerships",
    description:
      "Discover B2B partnership opportunities and enterprise clients tailored to your product's niche. Build a partnership pipeline before you even launch.",
    why: "B2B sales cycles are long and customer acquisition costs are high. Early strategic partnerships fast-track revenue without burning your runway on ads.",
  },
  GTM: {
    tagline: "Your personalized go-to-market playbook",
    description:
      "AI-generated GTM strategies mapped to your specific audience, market, and competitive landscape — from launch channels to pricing tactics and sequencing.",
    why: "Even the best-validated ideas fail without a clear launch strategy. A structured GTM plan cuts time-to-first-customer significantly.",
  },
  Membership: {
    tagline: "Premium tools for ambitious founders",
    description:
      "Tiered membership plans unlocking advanced AI models, priority scoring, white-glove report generation, and exclusive expert mentorship sessions.",
    why: "Access to institutional-quality tools shouldn't be reserved for funded startups. Scale your toolkit as your startup scales.",
  },
  Match: {
    tagline: "Meet your co-founder or advisor",
    description:
      "AI-powered matching connects you with co-founders, advisors, and mentors whose skills, goals, and work styles complement yours perfectly.",
    why: "Team quality is the #1 predictor of startup success. The right partner can be the difference between a pivot and a shutdown.",
  },
  Community: {
    tagline: "Build with a tribe of founders",
    description:
      "Join a curated network of builders, investors, and domain experts on similar journeys — share wins, get feedback, and find collaborators who get it.",
    why: "Isolation kills momentum. A strong founder community provides accountability, serendipitous connections, and the honest feedback that polished advisors won't give.",
  },
  Startup: {
    tagline: "From idea to launch, step by step",
    description:
      "An end-to-end launch toolkit that breaks your startup journey into actionable milestones — legal setup, MVP checklist, first-user acquisition, and beyond.",
    why: "Launching is overwhelming. A guided framework ensures nothing critical falls through the cracks, especially for first-time founders.",
  },
  Innovation: {
    tagline: "Stay ahead of the curve",
    description:
      "Track emerging trends, map your idea against global innovation signals, and identify whitespace before the market moves in.",
    why: "The gap between disruption and obsolescence is timing. Knowing what's coming next lets you position your startup before the crowd arrives.",
  },
  Reports: {
    tagline: "Institutional-grade market intelligence",
    description:
      "Deep-dive market research, competitive analysis, and industry benchmarks generated at startup speed — no expensive consultants required.",
    why: "Investors and partners expect data-backed decision-making. Access research-quality insights without the research-quality price tag.",
  },
  Revenue: {
    tagline: "Model your path to profitability",
    description:
      "Revenue projection and modeling tools calibrated to your specific business model — SaaS, marketplace, D2C, or services.",
    why: "Fundraising and strategic planning both demand financial clarity. Realistic models that hold up under scrutiny separate serious founders from the rest.",
  },
  Funding: {
    tagline: "A staged roadmap to capitalize your vision",
    description:
      "Structured funding guidance from bootstrapping and grants through angels, seed, and Series A — with readiness checklists at every milestone.",
    why: "Fundraising without a plan burns time and dilutes focus. A staged strategy keeps you capitalized and in control at every growth phase.",
  },
};

export default function Popup({ show, onClose, category }) {
  const { activeTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const info = category ? featureInfo[category.title] : null;

  const handleReachUs = () => {
    onClose();
    // Navigate to landing page and scroll to the "Get In Touch" contact section
    navigate("/landing", { state: { scrollTo: "getintouch" } });
  };

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, onClose]);

  if (!show || !category || !info) return null;

  const color = category.color || "#C1440E";

  return (
    <div
      className={`popup-overlay2 ${activeTheme === "dark" ? "dark-mode" : ""}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="popup-box popup-feature-box">

        <div className="popup-coming-soon">
          <Clock size={11} />
          Upcoming Feature
        </div>

        <div className="popup-header">
          <div className="popup-header-left">
            <div
              className="popup-feature-icon"
              style={{
                background: `linear-gradient(145deg, ${color}42, ${color}18)`,
                border: `1.5px solid ${color}55`,
                color: color,
              }}
            >
              {category.icon}
            </div>
            <div className="popup-header-text">
              <h2 style={{ color: color }}>{category.title}</h2>
              <p className="popup-tagline">{info.tagline}</p>
            </div>
          </div>
          <button className="popup-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="popup-divider" style={{ background: `${color}28` }} />

        <p className="popup-description">{info.description}</p>

        <div className="popup-why-box" style={{ borderLeft: `3px solid ${color}` }}>
          <span className="popup-why-label" style={{ color: color }}>Why it matters</span>
          <p className="popup-why-text">{info.why}</p>
        </div>

        <div className="popup-footer">
          <button
            className="popup-reach-btn"
            style={{ color: color, borderColor: `${color}55` }}
            onClick={handleReachUs}
          >
            Reach Us
          </button>
          <button
            className="popup-notify-btn"
            style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}bb 100%)` }}
            onClick={onClose}
          >
            Got it
          </button>
        </div>

      </div>
    </div>
  );
}
